'use client';

import type { SnackbarKey } from 'notistack';
import { closeSnackbar, enqueueSnackbar } from '@/providers/SnackbarProvider';
import { SNACKBAR_VARIANT } from '@/constants/notifications';
import type { PublicSearchSyncCompletedPayload } from '@/types/publicSearchSync';
import { upsertEntityHubConnection } from '@/lib/upsertEntityHubConnection';

const pendingSnackbarKeys = new Map<string, SnackbarKey>();

function normalizeSyncMessageId(syncMessageId: string) {
  return syncMessageId.trim().toLowerCase();
}

function parseCompletionPayload(
  raw: unknown,
): PublicSearchSyncCompletedPayload | null {
  if (!raw || typeof raw !== 'object') return null;

  const payload = raw as Record<string, unknown>;
  const syncMessageId = payload.syncMessageId ?? payload.SyncMessageId;
  if (typeof syncMessageId !== 'string' || !syncMessageId.trim()) return null;

  return {
    syncMessageId,
    orgId: Number(payload.orgId ?? payload.OrgId ?? 0),
    success: Boolean(payload.success ?? payload.Success),
    skipped: Boolean(payload.skipped ?? payload.Skipped),
    errorMessage: (payload.errorMessage ?? payload.ErrorMessage ?? null) as
      | string
      | null,
  };
}

function handlePublicSearchSyncCompleted(raw: unknown) {
  const payload = parseCompletionPayload(raw);
  if (!payload) return;

  const normalizedId = normalizeSyncMessageId(payload.syncMessageId);
  const snackbarKey = pendingSnackbarKeys.get(normalizedId);

  if (snackbarKey !== undefined) {
    closeSnackbar(snackbarKey);
    pendingSnackbarKeys.delete(normalizedId);
  }

  if (payload.skipped) {
    enqueueSnackbar(
      payload.errorMessage ??
        'Public search was not updated. This organisation may not be linked to a breed society.',
      { variant: SNACKBAR_VARIANT.WARNING },
    );
    return;
  }

  if (payload.success) {
    enqueueSnackbar('Public search updated', {
      variant: SNACKBAR_VARIANT.SUCCESS,
    });
    return;
  }

  enqueueSnackbar(
    payload.errorMessage ?? 'Public search update failed. Please try again.',
    { variant: SNACKBAR_VARIANT.ERROR },
  );
}

export function registerPublicSearchSyncListener(userGuid: string | undefined) {
  if (!userGuid) return;

  upsertEntityHubConnection.subscribePersistent(
    userGuid,
    'PublicSearchSyncCompleted',
    handlePublicSearchSyncCompleted,
  );
}

export function notifyPublicSearchSyncQueued(
  userGuid: string | undefined,
  syncMessageId?: string | null,
) {
  if (!userGuid || !syncMessageId) return;

  registerPublicSearchSyncListener(userGuid);

  const normalizedId = normalizeSyncMessageId(String(syncMessageId));
  const existingKey = pendingSnackbarKeys.get(normalizedId);
  if (existingKey !== undefined) {
    closeSnackbar(existingKey);
  }

  const key = enqueueSnackbar('Updating public search…', {
    variant: SNACKBAR_VARIANT.INFO,
    persist: true,
  });

  pendingSnackbarKeys.set(normalizedId, key);
}
